import React, {useCallback, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {useDispatch} from '../../../state/hooks';
import * as ViewHooks from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {getPanelSpec} from '../../../util/panelTypes';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {usePanelContext} from '../PanelContextProvider';
import {PanelPopupMode} from '../types';
import PanelEditor from './PanelEditor';

export const PanelEditorContainer = ({
  currentHeight,
  disableRunLinks,
  onOK: onOKProp,
}: {
  currentHeight: number;
  disableRunLinks?: boolean;
  onOK?: () => void;
}) => {
  const history = useHistory();
  const {panelBankConfigRef, workspaceSettingsRef} =
    usePanelsSharedRefsContext();

  const {
    isReadOnly,
    panelBankSectionConfigRef,
    panelRef,
    panel,
    panelPopupMode,
    setPanelPopupMode,
  } = usePanelContext();

  const panelSpec = useMemo(
    () => getPanelSpec(panel.viewType),
    [panel.viewType]
  );

  const initialConfigState = ViewHooks.usePart(panelRef);

  const dispatch = useDispatch();

  // TODO - refactor so we only define this function in reports
  // Only reports will hit this case since there's no association to panelBankSectionConfigRef there
  const onRemovePanel = useMemo(() => {
    if (!panelBankSectionConfigRef) {
      return;
    }

    return () =>
      dispatch(
        PanelBankSectionConfigActions.deletePanel(
          panelBankSectionConfigRef,
          panelRef,
          panelBankConfigRef,
          workspaceSettingsRef
        )
      );
  }, [
    dispatch,
    panelBankConfigRef,
    panelBankSectionConfigRef,
    panelRef,
    workspaceSettingsRef,
  ]);

  const onCancel = useCallback(() => {
    setPanelPopupMode(undefined);
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('panelId');
    history.replace(`${window.location.pathname}?${urlParams.toString()}`);
  }, [setPanelPopupMode, history]);

  const onOK = useCallback(() => {
    setPanelPopupMode(undefined);
    onOKProp?.();
  }, [setPanelPopupMode, onOKProp]);

  if (panelPopupMode == null) {
    return null;
  }

  // This can be triggered in workspace, full screen panel, and reports
  if (panelPopupMode === PanelPopupMode.EDIT) {
    return (
      <PanelEditor
        currentHeight={currentHeight}
        initialConfigState={initialConfigState}
        initialEditingState
        onCancel={onCancel}
        onOK={onOK}
        onRemove={onRemovePanel}
        panelRef={panelRef}
        panelSpec={panelSpec}
        readOnly={isReadOnly}
      />
    );
  }

  // This can be triggered in workspace, full screen panel, and reports
  if (panelPopupMode === PanelPopupMode.FULL_SCREEN) {
    return (
      <PanelEditor
        currentHeight={currentHeight}
        disableRunLinks={disableRunLinks}
        initialEditingState={false}
        onCancel={onCancel}
        onOK={onOK}
        onRemove={onRemovePanel}
        panelRef={panelRef}
        panelSpec={panelSpec}
        readOnly={isReadOnly}
      />
    );
  }

  // This can be triggered in published report with vega2 panels.
  // See PanelHeader for the specific checks that would trigger this.
  if (panelPopupMode === PanelPopupMode.VIEW_SPEC) {
    return (
      <PanelEditor
        currentHeight={currentHeight}
        initialConfigState={{editMode: true}}
        initialEditingState={true}
        onCancel={onCancel}
        onOK={onOK}
        onRemove={onRemovePanel}
        panelRef={panelRef}
        panelSpec={panelSpec}
        readOnly={isReadOnly}
      />
    );
  }

  return null;
};
